// SPDX-License-Identifier: GPL-3.0-or-later
// Gamepad input of the game page (DESIGN.md section 9, row B5).
// The page polls the pads once for each frame. A button maps to a game action or to pause.
// The map follows the "standard" layout: bumpers and triggers are the flippers.
// Held actions (flippers, plunger) stay down while at least one mapped button is down.
// Nudge, start and pause fire once for each press.
// Start and pause also work while a dialog is open: the page decides what they do there.

import type { InputAction, InputEvent } from "../src/shared/contracts.ts";

/** What a pad button does. An engine action, or a shell command. */
export type PadCommand = InputAction | "pause";

/** Button index (standard layout) to command. */
export const PAD_MAP: Readonly<Record<number, PadCommand>> = {
  0: "plunger", // bottom face button
  2: "nudge_up", // left face button
  4: "left_flipper", // left bumper
  5: "right_flipper", // right bumper
  6: "left_flipper", // left trigger
  7: "right_flipper", // right trigger
  8: "start", // back (select)
  9: "pause", // start (menu)
  12: "nudge_up", // d-pad up
  13: "plunger", // d-pad down
  14: "left_flipper", // d-pad left
  15: "right_flipper", // d-pad right
};

/** The part of a gamepad that the controller reads. */
export interface GamepadLike {
  readonly connected: boolean;
  readonly buttons: ReadonlyArray<{ readonly pressed: boolean }>;
}

/** The part of `navigator` that reads the pads. */
export interface PadSource {
  getGamepads?: () => ArrayLike<GamepadLike | null | undefined>;
}

/** Read the connected pads. A browser with no Gamepad API, or one that throws, gives no pads. */
export function padsFrom(source: PadSource | undefined): ArrayLike<GamepadLike | null | undefined> {
  try {
    return source?.getGamepads?.() ?? [];
  } catch {
    // A page with no gamepad permission can throw. No pad then.
    return [];
  }
}

/** What the page tells the controller. */
export interface GamepadDeps {
  /** Send one input to the engine. */
  readonly send: (ev: InputEvent) => void;
  /** Called for the pause button. */
  readonly onPause: () => void;
  /** Called for the start button. The page starts a game on the start and game over screens. */
  readonly onStart: () => void;
  /** True while a dialog is open. Then the game takes no button. */
  readonly modalOpen: () => boolean;
  /** True while the pause dialog is the open dialog. The pause button then resumes. */
  readonly pauseOpen: () => boolean;
  /** The connected pads. Often `navigator.getGamepads()`. */
  readonly pads: () => ArrayLike<GamepadLike | null | undefined>;
}

/** A gamepad controller. */
export interface GamepadController {
  /** Read the pads and send the changes. Call it once for each frame. */
  poll(): void;
  /** Release every held action. Call it on pause, on blur and on game over. */
  releaseAll(): void;
}

const HELD_ACTIONS: ReadonlySet<PadCommand> = new Set<PadCommand>([
  "left_flipper",
  "right_flipper",
  "plunger",
]);

/** What one poll saw on the pads. */
interface PadScan {
  /** Buttons ("pad:button") that are down now. */
  readonly down: Set<string>;
  /** Held actions that a button asks for now. */
  readonly wantHeld: Set<InputAction>;
  /** Commands with a new press. One set: two buttons of one command that go down together count as one press. */
  readonly edges: Set<PadCommand>;
}

/** Read the mapped buttons of one pad. `previous` holds the buttons that were down at the last poll. */
function scanPad(
  scan: PadScan,
  previous: ReadonlySet<string>,
  pad: GamepadLike,
  padIndex: number,
): void {
  for (const [indexText, command] of Object.entries(PAD_MAP)) {
    const index = Number(indexText);
    if (pad.buttons[index]?.pressed !== true) continue;
    const id = `${padIndex}:${index}`;
    scan.down.add(id);
    if (HELD_ACTIONS.has(command)) scan.wantHeld.add(command as InputAction);
    else if (!previous.has(id)) scan.edges.add(command);
  }
}

/** Read every connected pad. */
function scanPads(
  pads: ArrayLike<GamepadLike | null | undefined>,
  previous: ReadonlySet<string>,
): PadScan {
  const scan: PadScan = { down: new Set(), wantHeld: new Set(), edges: new Set() };
  for (let p = 0; p < pads.length; p += 1) {
    const pad = pads[p];
    if (pad?.connected === true) scanPad(scan, previous, pad, p);
  }
  return scan;
}

/** Fire the commands with a new press. */
function fireEdges(deps: GamepadDeps, edges: ReadonlySet<PadCommand>, modal: boolean): void {
  for (const command of edges) {
    if (command === "pause") {
      if (!modal || deps.pauseOpen()) deps.onPause();
    } else if (command === "start") {
      deps.onStart();
    } else if (!modal) {
      deps.send({ action: command, pressed: true });
    }
  }
}

/** Make the controller. */
export function createGamepadController(deps: GamepadDeps): GamepadController {
  /** Held actions that are down now. */
  const held = new Set<InputAction>();
  /** Buttons ("pad:button") that were down at the last poll. */
  let previous = new Set<string>();

  const release = (action: InputAction): void => {
    held.delete(action);
    deps.send({ action, pressed: false });
  };

  const press = (action: InputAction): void => {
    held.add(action);
    deps.send({ action, pressed: true });
  };

  /** A dialog takes the buttons. Held actions go up, and a press that began before stays ignored. */
  const syncHeld = (wantHeld: ReadonlySet<InputAction>, modal: boolean): void => {
    for (const action of [...held]) {
      if (modal || !wantHeld.has(action)) release(action);
    }
    if (modal) return;
    for (const action of wantHeld) {
      if (!held.has(action)) press(action);
    }
  };

  return {
    poll() {
      const scan = scanPads(deps.pads(), previous);
      previous = scan.down;
      const modal = deps.modalOpen();
      syncHeld(scan.wantHeld, modal);
      fireEdges(deps, scan.edges, modal);
    },
    releaseAll() {
      for (const action of [...held]) release(action);
    },
  };
}
